import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { Tooltip } from 'radix-ui';
import { useEffect, useState } from 'react';

import { Loader } from '~components';
import { queryClient } from '~config';
import { authService, trackException } from '~libs';
import { routeTree } from '~routes';

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const router = createRouter({ routeTree });

const App = () => {
  const [authInitialized, setAuthInitialized] = useState<boolean>(false);

  useEffect(() => {
    // This runs AFTER MsalAuthenticationTemplate has authenticated the user
    authService
      .initializeFromMsal()
      .then(() => {
        setAuthInitialized(true);
      })
      .catch((error) => {
        // TODO: handle initialization error with a proper error boundary or notification
        console.error('Failed to initialize auth from MSAL:', error);
        trackException(error, { source: 'app.authInitialization' });
        setAuthInitialized(true);
      });
  }, []);

  if (!authInitialized) {
    // Show a loading state while the authentication is being initialized
    return (
      <div className="grid h-full w-full place-items-center">
        <Loader orientation="vertical">We need to set things up for you...Please wait.</Loader>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      {/*
        The Tooltip.Provider is just a React context with no DOM
        Defining it at this level allows us to use Radix Tooltips anywhere in the app without needing to wrap individual components or pages,
        with consistent behavior (delayDuration, etc).
      */}
      <Tooltip.Provider delayDuration={150}>
        <RouterProvider router={router} />
      </Tooltip.Provider>
      {/* DevTools */}
      <TanStackRouterDevtools router={router} initialIsOpen={false} />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default App;
